import {
  ChannelFieldConfiguration,
  PostFields,
} from '@buffer-mono/reminders-config'

import { supportsInstagramLocationTagging } from '~publish/components/Composer/utils/supportsInstagramLocationTagging'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import { Service } from '~publish/legacy/constants/services/ServiceDefinitions'

export function getStickersToDisplay(args: {
  service: string
  updateType: string
}): PostFields[] {
  const { service, updateType } = args

  const serviceConfig =
    ChannelFieldConfiguration?.[service]?.postTypes?.[updateType]

  if (!serviceConfig) {
    return []
  }

  const { reminderFields, postFields } = serviceConfig

  const isAnyInstagramPersonalProfileSelected =
    AppStore.getSelectedProfiles().some(
      (profile) =>
        profile.serviceName === Service.Instagram &&
        profile.serviceType === 'profile',
    )

  return (
    reminderFields
      .filter((field) => !postFields.includes(field))
      // TODO: remove this after introducing channel type to ChannelFieldConfiguration
      .filter(
        (field) =>
          !(isAnyInstagramPersonalProfileSelected && field === 'products'),
      )
  )
}

function getFilteredFields(args: {
  service: string
  updateType: string
  isReminder: boolean
  stripLocationFields: boolean
}): PostFields[] {
  const { service, updateType, isReminder, stripLocationFields } = args

  const serviceConfig =
    ChannelFieldConfiguration[service]?.postTypes[updateType]

  if (!serviceConfig) {
    return []
  }

  const { reminderFields, postFields } = serviceConfig

  if (!isReminder) {
    return postFields
  }

  const fields = reminderFields.filter((field) => postFields.includes(field))

  if (service === 'instagram' && stripLocationFields) {
    return fields.filter(
      (field) =>
        ![PostFields.LOCATION_NAME, PostFields.LOCATION_ID].includes(field),
    )
  }

  return fields
}

export function getFieldsToDisplay(args: { draft: Draft }): PostFields[] {
  const { draft } = args

  // A single shared Instagram draft covers all selected IG channels, so the
  // location fields are gated on the first selected IG profile's capability
  // (same source-of-truth predicate as LocationComposerBar and the GraphQL
  // save path). No IG profile selected → nothing to strip.
  const instagramProfile = AppStore.getSelectedProfiles().find(
    (profile) => profile.service.name === 'instagram',
  )
  const stripLocationFields =
    instagramProfile != null &&
    !supportsInstagramLocationTagging(instagramProfile)

  const isReminder = draft.isReminder ?? false

  const fieldsToDisplay = getFilteredFields({
    service: draft.id,
    updateType: draft.updateType ?? 'post',
    isReminder,
    stripLocationFields,
  })
  const stickersToDisplay = isReminder
    ? draft.selectedStickers.filter((selectedSticker) =>
        ChannelFieldConfiguration[draft.id]?.postTypes[
          draft.updateType ?? 'post'
        ].reminderFields.includes(selectedSticker),
      )
    : []

  return [...stickersToDisplay, ...fieldsToDisplay]
}
